export type DsgvoProcessId =
  | 'kunden'
  | 'farbrezept'
  | 'kasse'
  | 'personal'
  | 'whatsapp'
  | 'video';

export type DsgvoTomId =
  | 'zutritt'
  | 'zugang'
  | 'zugriff'
  | 'weitergabe'
  | 'verfuegbarkeit'
  | 'trennung';

export interface DsgvoProcessDef {
  id: DsgvoProcessId;
  title: string;
  purpose: string;
  legalBasis: string;
  dataCategories: string;
  retention: string;
  recipients?: string;
  /** Requires cashSoftwareName when active */
  requiresCashSoftware?: boolean;
  /** Requires taxAdvisorName when active */
  requiresTaxAdvisor?: boolean;
  defaultActive: boolean;
}

export interface DsgvoTomDef {
  id: DsgvoTomId;
  title: string;
  description: string;
  defaultImplemented: boolean;
}

export interface DsgvoProcessState {
  id: DsgvoProcessId;
  active: boolean;
  note: string;
}

export interface DsgvoTomState {
  id: DsgvoTomId;
  implemented: boolean;
  note: string;
}

export const DSGVO_DOC_ID = 'PEPAPERS-COMPLIANCE-DSGVO-01';

export const DSGVO_DOC_TITLE =
  'Verfahrensdokumentation (Art. 30 DSGVO) inkl. TOMs (Art. 32 DSGVO)';
export const DSGVO_DOC_SUBTITLE = 'Friseur-/Salonbetrieb · Verzeichnis der Verarbeitungstätigkeiten';
export const DSGVO_PAGE2_BAND_LABEL = 'Seite 2 von 2 · Technische & organisatorische Maßnahmen';
export const DSGVO_CONFIRMATION_TEXT =
  'Der/die Unterzeichnende bestätigt, dass die Angaben dem tatsächlichen Stand der Verarbeitung im Betrieb entsprechen. Dieses Dokument dient als Dokumentationshilfe gemäß Art. 30 und Art. 32 DSGVO und ersetzt keine externe Rechts- oder Datenschutzberatung.';
export const DSGVO_DISCLAIMER_CHECKBOX =
  'Ich habe die Angaben geprüft. Es handelt sich um ein Standard-Muster ohne individuelle Datenschutzberatung; Verwendung auf eigene Verantwortung.';

export const DSGVO_DEFAULT_TOM_NOTE_OK = 'Dokumentiert & betrieblich angewiesen';
export const DSGVO_DEFAULT_TOM_NOTE_OPEN = 'Maßnahme noch offen / nachzuholen';

export const DSGVO_OPEN_CASH_LABEL =
  'Offene Ladenkasse / Handschriftliche Belegführung gem. GoBD';
export const DSGVO_SELF_ACCOUNTING_LABEL =
  'Interne Abwicklung durch Betriebsinhaber (keine externe Weitergabe)';

export const DSGVO_PROCESSES: DsgvoProcessDef[] = [
  {
    id: 'kunden',
    title: 'Kundenverwaltung & Terminbuchung',
    purpose: 'Terminplanung, Kundenstammdaten und Salonkommunikation zur Vertragserfüllung.',
    legalBasis: 'Art. 6 Abs. 1 lit. b DSGVO',
    dataCategories: 'Name, Kontaktdaten, Termin-/Buchungshistorie',
    retention: '24 Monate nach Inaktivität',
    recipients: 'Salonintern; ggf. Terminbuchungsdienstleister (AVV)',
    defaultActive: true,
  },
  {
    id: 'farbrezept',
    title: 'Farbrezepturen, Behandlungskartei & Sensible Daten',
    purpose:
      'Dokumentation von Behandlungen, Farbrezepturen und gesundheitsbezogenen Angaben (Einwilligung).',
    // Festgeklopft — kein Komma nach Abs, kein &
    legalBasis: 'Art. 6 Abs. 1 lit. a DSGVO und Art. 9 Abs. 2 lit. a DSGVO',
    dataCategories: 'Behandlungshistorie, Rezepturen, Allergie-/Gesundheitsangaben',
    retention: 'nach Widerruf / 3 Jahre Inaktivität',
    recipients: 'Salonintern',
    defaultActive: true,
  },
  {
    id: 'kasse',
    title: 'Kassenführung, Abrechnung & Zahlungsdienstleister',
    // Festgeklopft — "Kassenvorgänge" (nicht "Kasserivorgänge")
    purpose: 'Kassenvorgänge, Rechnungsstellung, TSE und steuerliche Aufbewahrung.',
    legalBasis: 'Art. 6 Abs. 1 lit. c DSGVO i.V.m. § 147 AO / GoBD',
    dataCategories: 'Zahlungsdaten, Belege, Rechnungsempfänger',
    retention: '10 Jahre',
    recipients: 'Zahlungsdienstleister, Finanzamt, ggf. Steuerberater',
    requiresCashSoftware: true,
    defaultActive: true,
  },
  {
    id: 'personal',
    title: 'Personalverwaltung & Lohnabrechnung',
    purpose: 'Beschäftigtendaten, Arbeitszeit, Lohn und Sozialversicherung.',
    // Festgeklopft — "i.V.m." (nicht "I.V.m.")
    legalBasis: 'Art. 6 Abs. 1 lit. b DSGVO i.V.m. § 26 BDSG',
    dataCategories: 'Personalstammdaten, Lohn-/Arbeitszeitdaten',
    retention: 'bis 10 Jahre (gesetzliche Aufbewahrung)',
    recipients: 'Steuerberater/Lohnbüro, Krankenkasse, Finanzamt',
    requiresTaxAdvisor: true,
    defaultActive: true,
  },
  {
    id: 'whatsapp',
    title: 'Salon-Kommunikation & WhatsApp Business',
    purpose: 'Kundenkommunikation über Messenger (nur mit Einwilligung).',
    legalBasis: 'Art. 6 Abs. 1 lit. a DSGVO',
    dataCategories: 'Telefonnummer, Chat-Inhalte, Terminabsprachen',
    retention: 'nach Widerruf / Zweckfortfall',
    recipients: 'Meta Platforms (WhatsApp) – Drittlandtransfer beachten',
    defaultActive: false,
  },
  {
    id: 'video',
    title: 'Videoüberwachung',
    purpose: 'Schutz von Eigentum und Personen (berechtigtes Interesse).',
    legalBasis: 'Art. 6 Abs. 1 lit. f DSGVO',
    dataCategories: 'Bildaufnahmen / Videostreams',
    retention: 'max. 72 Stunden',
    recipients: 'Salonintern; ggf. Sicherheitsdienstleister',
    defaultActive: false,
  },
];

export const DSGVO_TOMS: DsgvoTomDef[] = [
  {
    id: 'zutritt',
    title: 'Zutrittskontrolle',
    description: 'Salon/Büro verschlossen; kein unbefugter Zutritt zu Räumen mit personenbezogenen Daten.',
    defaultImplemented: true,
  },
  {
    id: 'zugang',
    title: 'Zugangskontrolle',
    description: 'Passwortschutz für Kasse/Tablets; Auto-Lock bei Nichtnutzung.',
    defaultImplemented: true,
  },
  {
    id: 'zugriff',
    title: 'Zugriffskontrolle',
    description: 'Rollenbasierte Rechte; Team sieht nur die für die Tätigkeit nötigen Daten/Termine.',
    defaultImplemented: true,
  },
  {
    id: 'weitergabe',
    title: 'Weitergabekontrolle',
    description: 'TLS/SSL-Verschlüsselung; gesicherter Transfer (z. B. DATEV) an Empfänger.',
    defaultImplemented: true,
  },
  {
    id: 'verfuegbarkeit',
    title: 'Verfügbarkeitskontrolle',
    description: 'Tägliche Backups; redundante Cloud-/TSE-Sicherung gegen Datenverlust.',
    defaultImplemented: true,
  },
  {
    id: 'trennung',
    title: 'Trennungskontrolle',
    description: 'Logische Trennung von Kunden-, Personal- und Buchhaltungsdaten.',
    defaultImplemented: true,
  },
];

export function createDefaultDsgvoProcessStates(): DsgvoProcessState[] {
  return DSGVO_PROCESSES.map((p) => ({
    id: p.id,
    active: p.defaultActive,
    note: '',
  }));
}

export function createDefaultDsgvoTomStates(): DsgvoTomState[] {
  return DSGVO_TOMS.map((t) => ({
    id: t.id,
    implemented: t.defaultImplemented,
    note: '',
  }));
}

export function getProcessDef(id: DsgvoProcessId): DsgvoProcessDef | undefined {
  return DSGVO_PROCESSES.find((p) => p.id === id);
}

export function getTomDef(id: DsgvoTomId): DsgvoTomDef | undefined {
  return DSGVO_TOMS.find((t) => t.id === id);
}
